import { Box, Stack } from '@mui/material';
import { SearchInput } from '../../../common/SearchInput';
import { globalSearchSx } from '../../../pages/canvas-pages/canvasPanelConsts';
import { EditColumnsSelector } from '../../../common/table/custom-columns-table/EditColumnsSelector';
import { Badge, colors, Typography } from '@sweep-io/sweep-design';
import { SortDisplayTypes, SweepSort } from '../../../common/SweepSort';
import { SortOrder } from '../../../common/types';
import { LayoutTopBarProps } from './types';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  cloneElement,
  ReactElement,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTopBarSearchText,
  setTopBarSort,
  clearTopBarState,
  selectTopBarSearchText,
} from '../../../../reducers/documentation/documentationTopBarReducer';
import pluralize from 'pluralize';

interface TopBarRendererProps extends LayoutTopBarProps {
  itemsCount: number;
  isExpandedMode: boolean;
  allowColumnsManipulation?: boolean;
}

// Helper function to add forceCompact prop to DocumentationFilters
const addForceCompactProp = (
  filtersJsx: React.ReactNode,
  forceCompact: boolean,
): React.ReactNode => {
  if (filtersJsx && typeof filtersJsx === 'object' && 'props' in filtersJsx) {
    const element = filtersJsx as ReactElement;
    return cloneElement(element, {
      ...element.props,
      forceCompact,
    });
  }
  return filtersJsx;
};
export const TopBarRenderer = memo(
  ({
    downloadCsvJsx,
    filtersJsx,
    searchPlaceholder,
    isExpandedMode,
    allowColumnsManipulation,
    itemsCount,
    label,
    sortOptions,
    pageHeader,
    hideFiltersAndSort,
    sortProps,
    showResultsCount,
  }: TopBarRendererProps) => {
    const dispatch = useDispatch();
    const searchText = useSelector(selectTopBarSearchText);
    const [localSearchText, setLocalSearchText] = useState(searchText ?? '');
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Width detection for responsive filters
    const [isNarrowView, setIsNarrowView] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setLocalSearchText(searchText ?? '');
    }, [searchText]);

    useEffect(() => {
      const handleResize = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          setIsNarrowView(width < 1050);
        }
      };

      handleResize();

      let resizeObserver: ResizeObserver;
      const currentContainer = containerRef.current;
      if (currentContainer) {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(currentContainer);
      }

      window.addEventListener('resize', handleResize);

      return () => {
        if (resizeObserver && currentContainer) {
          resizeObserver.disconnect();
        }
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    const debouncedSetSearchText = useCallback(
      (value: string) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
          dispatch(setTopBarSearchText({ searchText: value }));
        }, 200);
      },
      [dispatch],
    );

    const handleSearchChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setLocalSearchText(value); // Immediate UI update
        debouncedSetSearchText(value); // Debounced Redux dispatch
      },
      [debouncedSetSearchText],
    );

    const handleClearSearch = useCallback(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      setLocalSearchText('');
      dispatch(setTopBarSearchText({ searchText: '' }));
    }, [dispatch]);

    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        dispatch(clearTopBarState());
      };
    }, [dispatch]);

    const onSortChange = useCallback(
      (sortBy: string, sortOrder: SortOrder) => {
        dispatch(setTopBarSort({ sort: { sortBy, sortOrder } }));
      },
      [dispatch],
    );

    const renderSearchInput = useMemo(() => {
      return (
        <SearchInput
          TextFieldProps={{
            value: localSearchText, // Use local state for immediate UI updates
            autoFocus: true,
            onChange: handleSearchChange,
            placeholder: searchPlaceholder ?? 'Search',
            sx: {
              width: isExpandedMode ? '280px' : '100%',
            },
          }}
          withFixedMagnifyingGlassIcon
          size="small"
          onClearButtonClick={handleClearSearch}
        />
      );
    }, [localSearchText, searchPlaceholder, isExpandedMode, handleSearchChange, handleClearSearch]);

    // Render filters with forceCompact prop when narrow
    const renderFilters = useMemo(() => {
      if (!filtersJsx) return null;

      return addForceCompactProp(filtersJsx, isNarrowView);
    }, [isNarrowView, filtersJsx]);

    if (!isExpandedMode) {
      return (
        <>
          <InputWrapper isExpandedMode={isExpandedMode}>{renderSearchInput}</InputWrapper>
          <Stack
            pt={1}
            pb={1}
            direction="row"
            alignItems={filtersJsx ? 'baseline' : 'center'}
            gap={0.5}
            justifyContent={'space-between'}
            width={'100%'}
            pl={2}
            pr={2}
          >
            <Stack direction="row" height="28px" alignItems="center">
              <Typography variant={'caption'} color={colors.grey[700]}>
                {itemsCount} {pluralize(label, itemsCount)}
              </Typography>
            </Stack>

            {!hideFiltersAndSort && (
              <Stack direction={filtersJsx ? 'column' : 'row'} gap={1} alignItems="flex-end">
                {downloadCsvJsx && downloadCsvJsx}
                <Stack direction="row" gap={1}>
                  {filtersJsx && filtersJsx}
                  {sortOptions && sortProps && (
                    <SweepSort
                      selectedValue={sortProps?.sortBy}
                      onChange={(option) => onSortChange(option, sortProps?.sortOrder)}
                      options={sortOptions}
                      displayType={SortDisplayTypes.IconAndLabel}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </>
      );
    }

    return (
      <>
        {pageHeader && (
          <Box display="flex" alignItems="center" gap={1}>
            <Stack direction="row" gap={0.5} alignItems="center">
              <Typography variant="h3">{pageHeader}</Typography>
              <Badge label={itemsCount} variant="secondary" showZero />
            </Stack>
          </Box>
        )}

        <Stack>
          <InputWrapper isExpandedMode={isExpandedMode} ref={containerRef}>
            <Stack direction="row" alignItems="center" gap={1}>
              {renderSearchInput}
              {renderFilters}
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              {allowColumnsManipulation && <EditColumnsSelector />}
              {downloadCsvJsx && downloadCsvJsx}
            </Stack>
          </InputWrapper>

          {showResultsCount && (
            <Stack direction="row" alignItems="center" gap={0.5} pt={1} mb={-2}>
              <Typography variant="caption" color={colors.grey[700]}>
                Showing
              </Typography>
              <Typography
                variant="caption-medium"
                color={colors.grey[700]}
              >{`${itemsCount} ${pluralize('result', itemsCount)}`}</Typography>
            </Stack>
          )}
        </Stack>
      </>
    );
  },
);

const InputWrapper = React.forwardRef<
  HTMLDivElement,
  {
    isExpandedMode: boolean;
    children: React.ReactNode;
  }
>(({ isExpandedMode, children }, ref) => {
  return (
    <Stack
      ref={ref}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        ...globalSearchSx(!!isExpandedMode),
        pr: 2,
        pl: 2,
        pb: 1,
      }}
    >
      {children}
    </Stack>
  );
});
